import { SoftphoneTransport, SOFTPHONE_EVENTS } from './transport-contract'
import { configureReconnectPolicy, resetReconnectPolicy } from './reconnectPolicy'

/**
 * "The network is down for the next N connects", held at MODULE scope rather than on an instance.
 *
 * The store rebuilds the transport for every reconnect attempt - the whole point of an attempt is a
 * fresh client with a fresh token - so a failure armed on the instance a test can reach would be
 * thrown away by the first rebuild and every attempt would succeed. A test that wants to watch the
 * loop exhaust itself is describing the network, not one client, and the network outlives them.
 */
let pendingConnectFailures = 0
let armedConnectFailure = null

function _takeConnectFailure() {
  if (pendingConnectFailures <= 0) {
    return null
  }

  pendingConnectFailures -= 1

  return armedConnectFailure || new Error('Fake transport could not connect')
}

export function failNextFakeConnects(count = 1, error = null) {
  pendingConnectFailures = count
  armedConnectFailure = error
}

export function resetFakeConnectFailures() {
  pendingConnectFailures = 0
  armedConnectFailure = null
}

/**
 * In-memory transport used by unit tests, local development and e2e. It implements the full
 * contract and adds `simulate*` methods so a test can drive an inbound call without any SDK,
 * media devices or backend.
 */
export class FakeSoftphoneTransport extends SoftphoneTransport {
  constructor() {
    super()

    // The fake never calls a backend, so it needs no `login_token`.
    this.requiresToken = false
    this.isConnected = false
    this.isMuted = false
    this.currentCallId = null
    this.currentCallerNumber = null
    this.commands = []
  }

  async connect(token) {
    this.commands.push({ command: 'connect', token })

    const failure = _takeConnectFailure()

    if (failure) {
      throw failure
    }

    this.isConnected = true

    this._emit(SOFTPHONE_EVENTS.REGISTERED)
  }

  async disconnect() {
    this.commands.push({ command: 'disconnect' })
    this.isDisconnectRequested = true
    // The fake never calls a backend, so it needs no `login_token`.
    this.requiresToken = false
    this.isConnected = false
    this.currentCallId = null

    this._emitUnregistered({ requested: true })
  }

  async answer(callId) {
    this.commands.push({ command: 'answer', callId })
    this.currentCallId = callId

    this._emit(SOFTPHONE_EVENTS.ANSWERED, { callId, callerNumber: this.currentCallerNumber || null })
  }

  async reject(callId) {
    this.commands.push({ command: 'reject', callId })
    this.currentCallId = null

    this._emit(SOFTPHONE_EVENTS.ENDED, { callId, reason: 'declined' })
  }

  async hangup(callId) {
    this.commands.push({ command: 'hangup', callId })
    this.currentCallId = null

    this._emit(SOFTPHONE_EVENTS.ENDED, { callId, reason: 'hangup' })
  }

  async setMuted(isMuted) {
    this.commands.push({ command: 'setMuted', isMuted })
    this.isMuted = isMuted
  }

  /* --- test / dev drivers, not part of the contract --- */

  // campaignName defaults to null: like the real SDK, a transport has no campaign of its own.
  // callerNumber mirrors what Telnyx puts on the call options, so a test can drive the caller id.
  simulateIncomingCall({ callId = `fake-call-${Date.now()}`, campaignName = null, callerNumber = null } = {}) {
    this.currentCallId = callId
    this.currentCallerNumber = callerNumber

    this._emit(SOFTPHONE_EVENTS.INCOMING, { callId, campaignName, callerNumber })

    return callId
  }

  simulateRemoteHangup(reason = 'remote-hangup') {
    const callId = this.currentCallId

    this.currentCallId = null

    this._emit(SOFTPHONE_EVENTS.ENDED, { callId, reason })
  }

  simulateError(message = 'Fake transport error', code = 'FAKE_ERROR') {
    this._emit(SOFTPHONE_EVENTS.ERROR, { message, code })
  }

  /**
   * The SDK reporting one ending twice, which is what it really does: `hangup` and then `destroy`
   * both arrive as `callUpdate` and the transport maps both to ENDED. Observed 0.4s apart against
   * a live Telnyx session.
   */
  simulateRemoteHangupReportedTwice(reason = 'remote-hangup') {
    const callId = this.currentCallId

    this.currentCallId = null

    this._emit(SOFTPHONE_EVENTS.ENDED, { callId, reason })
    this._emit(SOFTPHONE_EVENTS.ENDED, { callId, reason: 'destroy' })
  }

  /**
   * The SDK re-announcing a call it re-synced, which is what a recovered socket does to a call that
   * `keepConnectionAliveOnSocketClose` kept alive: the same `active` notification arrives again for
   * a call that was never re-answered.
   */
  simulateCallStateResync() {
    if (!this.currentCallId) {
      return
    }

    this._emit(SOFTPHONE_EVENTS.ANSWERED, {
      callId: this.currentCallId,
      callerNumber: this.currentCallerNumber || null
    })
  }

  /**
   * Arms the next `count` connects to fail, so a test can drive the reconnect loop through a retry
   * or all the way to giving up. Instance methods over the module functions because the dev hook an
   * e2e test reaches is a transport instance.
   */
  failNextConnects(count = 1, error = null) {
    failNextFakeConnects(count, error)
  }

  resetConnectFailures() {
    resetFakeConnectFailures()
  }

  /**
   * Turns the reconnect waits down for a test. The real backoff spans about half a minute by
   * design; an e2e test that wants to see the give-up state should not spend half a minute
   * watching it, and one that wants to see the FIRST attempt should not be timing-sensitive.
   */
  configureReconnect(overrides = {}) {
    return configureReconnectPolicy(overrides)
  }

  resetReconnect() {
    return resetReconnectPolicy()
  }

  /**
   * The registration coming back on the SAME client, without the store having rebuilt anything -
   * the SDK's own socket recovery. The reconnect loop leaves room for it on purpose (its first
   * backoff is that room), so a test needs to be able to drive it.
   */
  simulateRegistered() {
    this.isConnected = true

    this._emit(SOFTPHONE_EVENTS.REGISTERED)
  }

  // The registration dropping on its own - a dead socket, a re-register that never landed - as
  // opposed to the UNREGISTERED `disconnect()` emits because the app asked to go offline. This is
  // the drop the store reconnects from, so it is the one that reports `requested: false`.
  simulateUnregistered() {
    this.isConnected = false

    this._emitUnregistered({ requested: false })
  }
}

export default FakeSoftphoneTransport
